import { useSelector } from 'react-redux';

// jl-design-system
import AddressForm from 'jl-design-system/components/address-form/AddressForm';
import { Message } from 'jl-design-system/components/message/Message';
import { SecondaryButton, UnderlinedButton } from 'jl-design-system/elements/button/Button';
import { getFormInitialValuesFromAddress } from 'jl-design-system/utils/address/address';

// Types
import { RootState } from 'types/RootState.types';
import { AddressRecordProps } from 'types/Address.types';
import { InvalidAddressFormProps } from './InvalidAddressForm.types';

// Config
import getInvalidAddressFormConfig from '@utils/form/configs/invalidAddress';
import invalidAddressFormState from './InvalidAddressForm.state';

// Components
import Container from '../container';

// Styles
import styles from './invalid-address-form.scss';

const InvalidAddressForm = ({
  addressBook = [],
  addressFormId = '',
  configExtras,
  destroyOnUnmount,
  getConfig,
  international,
  savedAddress = {},
  isBillingAddress,
  isPaypalExpress,
  maxAddressLines,
  messageBody,
  onChangeAddress,
  onCreateAddress = () => {},
  onUpdateAddress = () => {},
  onUpdateAddressOverride,
  showChangeAddressButton,
}: InvalidAddressFormProps) => {
  const {
    showInitialErrorMessage,
    isSignedIn,
  } = useSelector((state: RootState) => invalidAddressFormState(
    state,
    addressFormId,
  ));

  const initialValues = {
    countryCode: 'GB',
    ...getFormInitialValuesFromAddress(savedAddress),
  };

  const onAddressFormSubmit = (values: AddressRecordProps) => {
    if (onUpdateAddressOverride) {
      return onUpdateAddressOverride(values);
    }

    const selectedAddress = addressBook.find((addressRecord: AddressRecordProps) => addressRecord.id === values.id);

    const transformedValues = {
      ...values,
      id: values.id,
      contact: selectedAddress?.contact ?? false,
    };

    if (!savedAddress.id && isSignedIn) {
      return onCreateAddress({
        formValues: transformedValues,
        formId: addressFormId,
      });
    }

    return onUpdateAddress({
      formValues: transformedValues,
      formId: addressFormId,
    });
  };

  const changeAddressButton = {
    extra: true,
    name: 'incomplete-address-change-address-button',
    component: isBillingAddress ? UnderlinedButton : SecondaryButton,
    props: {
      'data-testid': 'delivery-address-change-button',
      children: 'Change address',
      onClick: onChangeAddress,
      className: isBillingAddress ? styles.changeAddressButtonBilling : undefined,
    },
  };

  return (
    <div className={styles.container}>
      <Container marginBottom="2" renderIf={showInitialErrorMessage} testId="invalid-address-error">
        <Message
          body={messageBody}
          title="Please update the highlighted fields below"
          type="error"
        />
      </Container>

      <AddressForm
        configExtras={{
          ...configExtras,
          changeAddressButton: showChangeAddressButton ? changeAddressButton : undefined,
        }}
        data-testid="invalid-address-form"
        destroyOnUnmount={destroyOnUnmount}
        formId={addressFormId}
        getConfig={getInvalidAddressFormConfig({
          getConfig,
          editMode: true,
          initialValues,
          isPaypalExpress,
        })}
        initialValues={initialValues}
        international={international}
        internationalMode={international}
        maxAddressLines={maxAddressLines}
        multiFormValidation
        onFormSubmit={onAddressFormSubmit}
        postcodeSearchFormVisible={false}
        scrollValidationMessageIntoView
        showErrorsOnInit
      />
    </div>
  );
};

export default InvalidAddressForm;
